import React, { useCallback, useState } from "react";
import { Review, ReviewFilterParams, ReviewReply, ReviewServices } from "@/services/bedrock";
import {
  SOURCE_SYSTEM_CONFIG,
  FEATURED_SHAREABILITY_MIN_SCORE,
  formatReviewCount,
  getTopLevelReviewReplies,
} from "@/components/reviews";
import { format } from "date-fns";
import { toast } from "sonner";
import {
  exportSectionedDataToCsv,
  exportToXlsx,
  type XlsxColumnDef,
  type XlsxTableSection,
} from "@/utils/xlsxExportHelpers";
import { buildReputationReportMeta, formatReviewExportFilters } from "../../reportExportHelpers";
import { ReputationExportMenu } from "../../ReputationExportMenu";
import { useSocialCoachSession } from "@/utils";

const EXPORT_PAGE_SIZE = 100;

type ReviewExportRow = {
  Reviewer: string;
  Email: string;
  Player: string;
  "Player ID": string;
  Regions: string;
  Branches: string;
  Source: string;
  Stars: number | "";
  NPS: number | "";
  "10-pt": number | "";
  Recommendation: string;
  Sentiment: string;
  Content: string;
  "Condensed Quote": string;
  Featured: string;
  "Social Score": number | "";
  "Approved for Sharing": string;
  "Reply Status": string;
  Replies: string;
  Date: string;
  Link: string;
};

const REVIEW_COLUMNS: XlsxColumnDef[] = [
  { key: "Reviewer", header: "Reviewer", type: "string", wch: 22 },
  { key: "Email", header: "Email", type: "string", wch: 28 },
  { key: "Player", header: "Player", type: "string", wch: 22 },
  { key: "Player ID", header: "Player ID", type: "string", wch: 20 },
  { key: "Regions", header: "Regions", type: "string", wch: 24 },
  { key: "Branches", header: "Branches", type: "string", wch: 24 },
  { key: "Source", header: "Source", type: "string", wch: 14 },
  { key: "Stars", header: "Stars", type: "integer", wch: 8 },
  { key: "NPS", header: "NPS", type: "integer", wch: 8 },
  { key: "10-pt", header: "10-pt", type: "decimal", wch: 8 },
  { key: "Recommendation", header: "Recommendation", type: "string", wch: 16 },
  { key: "Sentiment", header: "Sentiment", type: "string", wch: 12 },
  { key: "Content", header: "Content", type: "string", wch: 50 },
  { key: "Condensed Quote", header: "Condensed Quote", type: "string", wch: 40 },
  { key: "Featured", header: "Featured", type: "string", wch: 10 },
  { key: "Social Score", header: "Social Score", type: "integer", wch: 12 },
  { key: "Approved for Sharing", header: "Approved for Sharing", type: "string", wch: 18 },
  { key: "Reply Status", header: "Reply Status", type: "string", wch: 14 },
  { key: "Replies", header: "Replies", type: "string", wch: 50 },
  { key: "Date", header: "Date", type: "string", wch: 14 },
  { key: "Link", header: "Link", type: "string", wch: 36 },
];

type ReviewsExportButtonProps = {
  toQueryParams: () => ReviewFilterParams;
  filterLabels?: string[];
  playerLabel?: string | null;
  totalCount?: number | null;
  loading?: boolean;
  disabled?: boolean;
};

export function ReviewsExportButton({
  toQueryParams,
  filterLabels,
  playerLabel,
  totalCount,
  loading = false,
  disabled,
}: ReviewsExportButtonProps) {
  const [isExporting, setIsExporting] = useState(false);
  const { session } = useSocialCoachSession();
  const companyName = session?.companyName;
  const hasReviews = (totalCount ?? 0) > 0;
  const label = totalCount == null ? "Export reviews" : `Export ${formatReviewCount(totalCount)}`;

  const fetchAllReviewsForExport = useCallback(
    async (toastId: string | number) => {
      const { perPage: _perPage, ...filterParams } = toQueryParams();
      const allReviews: Review[] = [];
      let page = 1;
      let totalPages = 1;

      while (page <= totalPages) {
        const result = await ReviewServices.getReviews({
          ...filterParams,
          page,
          perPage: EXPORT_PAGE_SIZE,
          // Preserve nested player fields from toQueryParams while also requesting replies.
          extraFields: {
            ...(typeof filterParams.extraFields === "object" && !Array.isArray(filterParams.extraFields)
              ? filterParams.extraFields
              : {}),
            review_replies: true,
          },
        });
        allReviews.push(...(result.content ?? []));
        totalPages = result.pagination?.totalPages ?? 1;
        const percentComplete = totalPages > 0 ? (page / totalPages) * 100 : 100;
        toast.loading(`Exporting... (${percentComplete.toFixed(0)}%)`, { id: toastId });
        page += 1;
      }

      return allReviews;
    },
    [toQueryParams]
  );

  const handleExport = useCallback(
    async (formatType: "xlsx" | "csv") => {
      setIsExporting(true);
      const toastId = toast.loading("Exporting... (0%)");
      try {
        const params = toQueryParams();
        const allReviews = await fetchAllReviewsForExport(toastId);
        if (!allReviews.length) {
          toast.info("No reviews to export", { id: toastId });
          return;
        }

        toast.loading("Building export file...", { id: toastId });
        const exportData = allReviews.map(transformReviewForExport);
        const section: XlsxTableSection = {
          title: "Reviews",
          columns: REVIEW_COLUMNS,
          rows: exportData,
        };
        const meta = buildReputationReportMeta({
          reportName: "SocialCoach Reviews Report",
          description: "Review inbox export for the currently applied filters.",
          companyName,
          filters: filterLabels ?? formatReviewExportFilters(params, { playerLabel }),
          totalRecords: exportData.length,
        });
        const filename = `Reviews_${format(new Date(), "yyyy-MM-dd")}`;

        if (formatType === "csv") {
          exportSectionedDataToCsv(filename, meta, [section]);
        } else {
          await exportToXlsx(filename, [{ name: "Reviews", meta, sections: [section] }]);
        }

        toast.success(`Exported ${exportData.length} review${exportData.length === 1 ? "" : "s"}`, { id: toastId });
      } catch {
        toast.error("Failed to export reviews. Please try again.", { id: toastId });
      } finally {
        setIsExporting(false);
      }
    },
    [companyName, fetchAllReviewsForExport, filterLabels, playerLabel, toQueryParams]
  );

  return (
    <ReputationExportMenu
      label={label}
      isExporting={isExporting}
      disabled={disabled || loading || !hasReviews}
      onExport={handleExport}
    />
  );
}

function formatExportRecommendation(review: Review): string {
  if (review.sourceSentiment === "positive") return "Recommended";
  if (review.sourceSentiment === "negative") return "Not Recommended";
  return "";
}

function formatExportApprovedForSharing(review: Review): string {
  if (review.approvedForSocial == null) return "";
  return review.approvedForSocial ? "Yes" : "No";
}

function formatExportReplyStatus(review: Review): string {
  if (review.replied) return "Replied";
  if (review.replySkipped) return "No reply needed";
  const isInternal = SOURCE_SYSTEM_CONFIG[review.sourceSystem]?.isInternal;
  return isInternal ? "Unreplied" : "Needs Reply";
}

function stripHtml(value: string): string {
  return value
    .replace(/<br\s*\/?>/gi, "\n")
    .replace(/<\/p>/gi, "\n")
    .replace(/<[^>]+>/g, "")
    .replace(/&nbsp;/gi, " ")
    .replace(/&amp;/gi, "&")
    .replace(/&lt;/gi, "<")
    .replace(/&gt;/gi, ">")
    .replace(/&quot;/gi, '"')
    .replace(/\n{3,}/g, "\n\n")
    .trim();
}

function flattenReviewReplies(replies: ReviewReply[], depth = 0): string[] {
  return replies.flatMap(reply => {
    const author = reply.displayName || "Reply";
    const date = reply.publishedAt ? format(new Date(reply.publishedAt), "MMM d, yyyy") : null;
    const content = reply.content ? stripHtml(reply.content) : "";
    const prefix = depth > 0 ? `${"— ".repeat(depth)}` : "";
    const header = date ? `${author} (${date})` : author;
    const line = content ? `${prefix}${header}: ${content}` : `${prefix}${header}`;
    return [line, ...flattenReviewReplies(reply.reviewReplies ?? [], depth + 1)];
  });
}

function formatExportReplies(review: Review): string {
  const topLevel = getTopLevelReviewReplies(review.reviewReplies);
  if (!topLevel.length) return "";
  return flattenReviewReplies(topLevel).join("\n");
}

function transformReviewForExport(review: Review): ReviewExportRow {
  const publishedAt = review.publishedAt ? new Date(review.publishedAt) : null;
  const isFeatured =
    review.aiShareabilityScore != null && review.aiShareabilityScore >= FEATURED_SHAREABILITY_MIN_SCORE;

  return {
    Reviewer: review.displayName || "Anonymous",
    Email: review.email || "",
    Player: review.player?.fullName || "Unassigned",
    "Player ID": review.player?.guid || "",
    Regions: review.player?.regionNames?.filter(Boolean).join(" | ") || "",
    Branches: review.player?.branchNames?.filter(Boolean).join(" | ") || "",
    Source: SOURCE_SYSTEM_CONFIG[review.sourceSystem]?.name ?? review.sourceSystem,
    Stars: review.starRating != null ? Number(review.starRating) : "",
    NPS: review.npsScore != null ? Number(review.npsScore) : "",
    "10-pt": review.tenPointRating != null ? Number(review.tenPointRating) : "",
    Recommendation: formatExportRecommendation(review),
    Sentiment: review.sentiment ? review.sentiment.charAt(0).toUpperCase() + review.sentiment.slice(1) : "",
    Content: review.content || "",
    "Condensed Quote": review.condensedQuote || "",
    Featured: isFeatured ? "Yes" : "No",
    "Social Score": review.aiShareabilityScore != null ? Number(review.aiShareabilityScore) : "",
    "Approved for Sharing": formatExportApprovedForSharing(review),
    "Reply Status": formatExportReplyStatus(review),
    Replies: formatExportReplies(review),
    Date: publishedAt ? format(publishedAt, "MMM d, yyyy") : "",
    Link: review.link || "",
  };
}
